import * as React from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, GraduationCap, WarningCircle } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import { useAuth } from '../../../../../providers/auth';
import { IEmpresaOption } from '../../../../../hooks/useContaCilImportar';
import { useContaCilResumo } from '../../../../../hooks/useContaCilLancamentos';
import {
  IAutopromoverStats,
  IGoldenRunHistoricoItem,
  IGoldenRunResult,
  ISchoolAiDashboard,
  useContaCilAutopromover,
  useContaCilEmbeddingsReembed,
  useContaCilEmbeddingsStatus,
  useContaCilGoldenComparar,
  useContaCilGoldenHistorico,
  useContaCilGoldenRemover,
  useContaCilGoldenRun,
  useContaCilGoldenRunMotor,
  useContaCilGoldenSet,
  useContaCilIaProgresso,
  useContaCilMemoriaGlobalAgregar,
  useContaCilMemoriaGlobalStatus,
  useContaCilPromoverMemoria,
  useContaCilSchoolAiAuditor,
  useContaCilSchoolAiDashboard,
} from '../../../../../hooks/useContaCilSchoolAi';
import { EmpresaPicker } from '../components/EmpresaPicker';
import { AbaMemoria } from '../empresas/components/AbaMemoria';
import { ProgressoConciliacao } from '../conciliar/components/ProgressoConciliacao';
import { useAplicarEmpresaParam } from '../useEmpresaParam';
import { ResiduoTab } from './components/ResiduoClusters';
import { FilaSugeridosTab } from './components/FilaSugeridosTab';
import { DivergenciasTab } from './components/DivergenciasTab';

const C = {
  violet: '#8B5CF6',
  amber: '#F59E0B',
  green: '#10B981',
  red: '#EF4444',
  ink: '#0F172A',
  muted: '#64748B',
  subtle: '#94A3B8',
};

const fmtNum = (v: number) => new Intl.NumberFormat('pt-BR').format(v);

type Tab = 'residuo' | 'fila' | 'aprendeu' | 'divergencias' | 'auditor' | 'prova' | 'progresso';

// ----------------------------------------------------------------------------
// Sprint 1 — Dashboard home: 4 cards (Hoje / Atencao / Aprendizado / Tendencia)
// ----------------------------------------------------------------------------

const fmtPct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);

const fmtUSD = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

// Mini sparkline SVG — 30 dias da taxa de conciliacao por dia.
function MiniSparkline({ pts, color }: { pts: number[]; color: string }) {
  if (!pts.length) return null;
  const w = 200;
  const h = 36;
  const max = Math.max(1, ...pts);
  const step = w / Math.max(1, pts.length - 1);
  const d = pts
    .map((v, i) => {
      const x = i * step;
      const y = h - (v / max) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <Box as="svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" w="100%" h="36px">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </Box>
  );
}

function HojeCard({ data }: { data: ISchoolAiDashboard['hoje'] }) {
  return (
    <GlassCard intensity="medium" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          color={C.subtle}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Hoje (24h)
        </Text>
        <Badge bg="rgba(16,185,129,0.12)" color={C.green} borderRadius="full" px={2} fontSize="9px">
          ao vivo
        </Badge>
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={C.ink} lineHeight="1">
        {fmtNum(data.processados)}
      </Text>
      <Text fontSize="11px" color={C.muted} mb={3}>
        processados · {fmtNum(data.conciliados)} conciliados · {fmtNum(data.pendentes)} pendentes
      </Text>
      <SimpleGrid columns={2} spacing={2}>
        <Box>
          <Text fontSize="10px" color={C.subtle}>
            via IA
          </Text>
          <Text fontSize="14px" fontWeight="600" color={C.violet}>
            {fmtNum(data.via_ia)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="10px" color={C.subtle}>
            via Regra
          </Text>
          <Text fontSize="14px" fontWeight="600" color={C.ink}>
            {fmtNum(data.via_regra)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="10px" color={C.subtle}>
            via Memória
          </Text>
          <Text fontSize="14px" fontWeight="600" color={C.ink}>
            {fmtNum(data.via_memoria)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="10px" color={C.subtle}>
            via Manual
          </Text>
          <Text fontSize="14px" fontWeight="600" color={C.ink}>
            {fmtNum(data.via_manual)}
          </Text>
        </Box>
      </SimpleGrid>
      <Flex
        mt={3}
        pt={2}
        borderTopWidth="1px"
        borderColor="rgba(15,23,42,0.06)"
        justify="space-between"
      >
        <Text fontSize="10px" color={C.muted}>
          custo IA · {fmtUSD(data.custo_ia_usd)}
        </Text>
        <Text fontSize="10px" color={C.muted}>
          {fmtNum(data.eventos_total)} eventos
        </Text>
      </Flex>
    </GlassCard>
  );
}

function AtencaoCard({
  data,
  onClick,
}: {
  data: ISchoolAiDashboard['atencao'];
  onClick?: () => void;
}) {
  const total =
    data.memorias_conflito +
    data.memorias_quarentena +
    data.regras_alto_revert +
    data.residuos_recentes_7d;
  const isHealthy = total === 0;
  return (
    <GlassCard
      intensity="medium"
      p={4}
      as={onClick ? 'button' : 'div'}
      onClick={onClick}
      textAlign="left"
      w="full"
      transition="all 0.15s"
      _hover={onClick ? { transform: 'translateY(-1px)' } : undefined}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          color={C.subtle}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Atenção
        </Text>
        <Badge
          bg={isHealthy ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)'}
          color={isHealthy ? C.green : C.red}
          borderRadius="full"
          px={2}
          fontSize="9px"
        >
          {isHealthy ? 'tudo OK' : `${total} sinais`}
        </Badge>
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={isHealthy ? C.green : C.red} lineHeight="1">
        {fmtNum(total)}
      </Text>
      <Text fontSize="11px" color={C.muted} mb={3}>
        precisa de revisão humana
      </Text>
      <Flex direction="column" gap={1}>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>memórias em conflito</Text>
          <Text fontWeight="600" color={data.memorias_conflito > 0 ? C.red : C.subtle}>
            {fmtNum(data.memorias_conflito)}
          </Text>
        </Flex>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>memórias em quarentena</Text>
          <Text fontWeight="600" color={data.memorias_quarentena > 0 ? C.amber : C.subtle}>
            {fmtNum(data.memorias_quarentena)}
          </Text>
        </Flex>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>regras com revert &gt; 25%</Text>
          <Text fontWeight="600" color={data.regras_alto_revert > 0 ? C.red : C.subtle}>
            {fmtNum(data.regras_alto_revert)}
          </Text>
        </Flex>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>resíduos últimos 7d</Text>
          <Text fontWeight="600" color={data.residuos_recentes_7d > 0 ? C.amber : C.subtle}>
            {fmtNum(data.residuos_recentes_7d)}
          </Text>
        </Flex>
      </Flex>
    </GlassCard>
  );
}

function AprendizadoCard({
  data,
  onClick,
  empresaId,
}: {
  data: ISchoolAiDashboard['aprendizado'];
  onClick?: () => void;
  empresaId?: number;
}) {
  const toast = useToast();
  const autopromover = useContaCilAutopromover(empresaId ?? 0);
  const [lastStats, setLastStats] = React.useState<IAutopromoverStats | null>(null);

  const handleAutopromover = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!empresaId) return;
    try {
      const s = await autopromover.mutateAsync();
      setLastStats(s);
      const total = s.memorias_promovidas + s.regras_criadas_auto + s.lancamentos_liberados;
      toast({
        status: total > 0 ? 'success' : 'info',
        title: total > 0 ? `${total} ações automáticas` : 'Nada a promover',
        description: `${s.memorias_promovidas} memórias · ${s.regras_criadas_auto} regras criadas · ${s.lancamentos_liberados} retentativas`,
        duration: 5000,
      });
    } catch {
      toast({ status: 'error', title: 'Falha no job' });
    }
  };

  return (
    <GlassCard
      intensity="medium"
      p={4}
      // NAO renderizar como <button>: este card contem o botao Autopromover
      // e <button> aninhado em <button> e HTML invalido (o parser realoca o
      // interno e quebra layout/stopPropagation). Div com semantica de botao.
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      cursor={onClick ? 'pointer' : undefined}
      textAlign="left"
      w="full"
      transition="all 0.15s"
      _hover={onClick ? { transform: 'translateY(-1px)' } : undefined}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          color={C.subtle}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Aprendizado
        </Text>
        <Badge
          bg="rgba(139,92,246,0.12)"
          color={C.violet}
          borderRadius="full"
          px={2}
          fontSize="9px"
        >
          motor IA
        </Badge>
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={C.violet} lineHeight="1">
        {fmtPct(data.taxa_acerto_atual)}
      </Text>
      <Text fontSize="11px" color={C.muted} mb={3}>
        taxa de acerto da IA
      </Text>
      <Flex direction="column" gap={1}>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>memórias confiáveis</Text>
          <Text fontWeight="600" color={C.ink}>
            {fmtNum(data.memorias_confiaveis)}
          </Text>
        </Flex>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>regras IA ativas</Text>
          <Text fontWeight="600" color={C.ink}>
            {fmtNum(data.regras_ia_aceitas)}
          </Text>
        </Flex>
        <Flex justify="space-between" fontSize="11px">
          <Text color={C.muted}>correções humanas (7d)</Text>
          <Text fontWeight="600" color={data.correcoes_humanas_7d > 0 ? C.green : C.subtle}>
            {fmtNum(data.correcoes_humanas_7d)}
          </Text>
        </Flex>
      </Flex>
      {empresaId && (
        <Flex
          mt={3}
          pt={2}
          borderTopWidth="1px"
          borderColor="rgba(15,23,42,0.06)"
          align="center"
          justify="space-between"
        >
          {lastStats ? (
            <Text fontSize="9px" color={C.subtle}>
              últ.:{' '}
              {lastStats.memorias_promovidas +
                lastStats.regras_criadas_auto +
                lastStats.lancamentos_liberados}{' '}
              ações
            </Text>
          ) : (
            <Text fontSize="9px" color={C.subtle}>
              Onda 2 — Sprint 4
            </Text>
          )}
          <Button
            size="xs"
            variant="ghost"
            color={C.violet}
            fontWeight="600"
            isLoading={autopromover.isPending}
            loadingText="Rodando"
            onClick={handleAutopromover}
            _hover={{ bg: 'rgba(139,92,246,0.10)' }}
          >
            ⚡ Autopromover
          </Button>
        </Flex>
      )}
    </GlassCard>
  );
}

function TendenciaCard({ serie }: { serie: ISchoolAiDashboard['serie_30d'] }) {
  // Dias sem processamento vem com taxa null — pintar como 0% criava vales
  // falsos na sparkline; o dia sem dado simplesmente nao entra na linha.
  const taxa = serie.filter(s => s.taxa != null).map(s => s.taxa as number);
  const proc = serie.map(s => s.processados);
  const totalProc = proc.reduce((a, b) => a + b, 0);
  const totalConc = serie.reduce((a, s) => a + s.conciliados, 0);
  const taxaMedia = totalProc > 0 ? totalConc / totalProc : null;
  // delta = ultima semana vs 4ª semana atrás (média)
  const ult7 = serie.slice(-7);
  const ant7 = serie.slice(-14, -7);
  const taxa7 = (() => {
    const t = ult7.reduce((a, s) => a + s.processados, 0);
    const c = ult7.reduce((a, s) => a + s.conciliados, 0);
    return t > 0 ? c / t : null;
  })();
  const taxa14 = (() => {
    const t = ant7.reduce((a, s) => a + s.processados, 0);
    const c = ant7.reduce((a, s) => a + s.conciliados, 0);
    return t > 0 ? c / t : null;
  })();
  const delta = taxa7 != null && taxa14 != null ? taxa7 - taxa14 : null;
  return (
    <GlassCard intensity="medium" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          color={C.subtle}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Tendência (30d)
        </Text>
        {delta != null && (
          <Badge
            bg={delta >= 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)'}
            color={delta >= 0 ? C.green : C.red}
            borderRadius="full"
            px={2}
            fontSize="9px"
          >
            {delta >= 0 ? '↑' : '↓'} {Math.abs(Math.round(delta * 100))}pp
          </Badge>
        )}
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={C.ink} lineHeight="1">
        {fmtPct(taxaMedia)}
      </Text>
      <Text fontSize="11px" color={C.muted} mb={3}>
        média 30d · {fmtNum(totalProc)} processados
      </Text>
      <Box mb={1}>
        <MiniSparkline pts={proc} color={C.green} />
      </Box>
      <Box>
        <MiniSparkline pts={taxa} color={C.violet} />
      </Box>
      <Flex gap={3} mt={1} fontSize="9px" color={C.subtle}>
        <Flex align="center" gap={1}>
          <Box w="8px" h="2px" bg={C.green} />
          <Text>processados/dia</Text>
        </Flex>
        <Flex align="center" gap={1}>
          <Box w="8px" h="2px" bg={C.violet} />
          <Text>taxa conciliação</Text>
        </Flex>
      </Flex>
    </GlassCard>
  );
}

// ----------------------------------------------------------------------------
// Sprint 2 — Aba Auditor: drift de memoria + regras decaindo + anomalias
// ----------------------------------------------------------------------------

const fmtData = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function AuditorTab({ empresaId }: { empresaId: number }) {
  const auditor = useContaCilSchoolAiAuditor(empresaId);
  const toast = useToast();
  const promover = useContaCilPromoverMemoria();
  const { data } = auditor;

  const handlePromover = async (id: number) => {
    try {
      await promover.mutateAsync(id);
      toast({ status: 'success', title: 'Memória promovida' });
    } catch {
      toast({ status: 'error', title: 'Falha ao promover memória' });
    }
  };

  if (auditor.isLoading) {
    return (
      <GlassCard intensity="medium" p={6}>
        <Text fontSize="sm" color={C.muted}>
          Carregando auditor…
        </Text>
      </GlassCard>
    );
  }

  // Erro de rede NÃO pode renderizar "Sistema saudável" (falso negativo
  // perigoso num auditor) — ramo de erro explícito antes do sucesso.
  if (auditor.isError) {
    return (
      <GlassCard intensity="medium" p={6}>
        <Flex align="center" gap={2}>
          <WarningCircle size={18} color="#EF4444" weight="fill" />
          <Text fontSize="sm" color={C.ink} fontWeight="600">
            Não foi possível carregar o auditor.
          </Text>
        </Flex>
        <Text fontSize="12px" color={C.muted} mt={1}>
          Verifique a conexão e recarregue a página.
        </Text>
      </GlassCard>
    );
  }

  const totalSinais =
    (data?.drift_memoria.length ?? 0) +
    (data?.regras_decaindo.length ?? 0) +
    (data?.anomalias_cluster.length ?? 0);

  return (
    <Flex direction="column" gap={4}>
      {totalSinais === 0 && (
        <GlassCard intensity="medium" p={6}>
          <Flex align="center" gap={3}>
            <Box
              w="36px"
              h="36px"
              borderRadius="full"
              bg="rgba(16,185,129,0.12)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="20px">✓</Text>
            </Box>
            <Box>
              <Text fontWeight="700" color={C.ink}>
                Sistema saudável
              </Text>
              <Text fontSize="11px" color={C.muted}>
                Nenhum drift de memória, regra decaindo ou cluster anômalo nos últimos 30 dias.
              </Text>
            </Box>
          </Flex>
        </GlassCard>
      )}

      {/* Anomalias */}
      {data && data.anomalias_cluster.length > 0 && (
        <GlassCard intensity="medium" p={5}>
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <Text fontWeight="700" color={C.ink}>
                Anomalias de cluster
              </Text>
              <Text fontSize="11px" color={C.muted}>
                Contrapartes com volume {'>'}=1.5× da mediana 30d — extrato novo? bug? fraude?
              </Text>
            </Box>
            <Badge bg="rgba(245,158,11,0.12)" color={C.amber} borderRadius="full" px={2}>
              {data.anomalias_cluster.length}
            </Badge>
          </Flex>
          <Flex direction="column" gap={2}>
            {data.anomalias_cluster.map(a => (
              <Flex
                key={`${a.contraparte_norm}-${a.dc}`}
                p={3}
                borderRadius="lg"
                bg="rgba(245,158,11,0.06)"
                borderWidth="1px"
                borderColor="rgba(245,158,11,0.2)"
                justify="space-between"
                align="center"
                gap={3}
              >
                <Box flex={1} minW={0}>
                  <Text fontSize="13px" fontWeight="600" color={C.ink} noOfLines={1}>
                    {a.contraparte_exibe || a.contraparte_norm}{' '}
                    <Badge ml={1} fontSize="9px">
                      {a.dc}
                    </Badge>
                  </Text>
                  <Text fontSize="11px" color={C.muted}>
                    {a.qtd_atual} lançamento(s) · mediana 30d: {a.mediana_30d.toFixed(1)}
                  </Text>
                </Box>
                <Box textAlign="right">
                  <Text fontSize="18px" fontWeight="700" color={C.amber}>
                    {a.razao.toFixed(1)}×
                  </Text>
                  <Text fontSize="9px" color={C.muted}>
                    vs mediana
                  </Text>
                </Box>
              </Flex>
            ))}
          </Flex>
        </GlassCard>
      )}

      {/* Drift de memória */}
      {data && data.drift_memoria.length > 0 && (
        <GlassCard intensity="medium" p={5}>
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <Text fontWeight="700" color={C.ink}>
                Drift de memória (30d)
              </Text>
              <Text fontSize="11px" color={C.muted}>
                Memórias que mudaram recentemente — re-aprendizado ou correção humana
              </Text>
            </Box>
            <Badge bg="rgba(139,92,246,0.12)" color={C.violet} borderRadius="full" px={2}>
              {data.drift_memoria.length}
            </Badge>
          </Flex>
          <Flex direction="column" gap={2}>
            {data.drift_memoria.map(m => (
              <Flex
                key={m.memoria_id}
                p={3}
                borderRadius="lg"
                bg="rgba(255,255,255,0.55)"
                borderWidth="1px"
                borderColor="rgba(15,23,42,0.06)"
                justify="space-between"
                align="center"
                gap={3}
              >
                <Box flex={1} minW={0}>
                  <Flex align="center" gap={2}>
                    <Text fontSize="13px" fontWeight="600" color={C.ink} noOfLines={1}>
                      {m.contraparte_norm}
                    </Text>
                    <Badge fontSize="9px" colorScheme={m.dc === 'D' ? 'red' : 'green'}>
                      {m.dc}
                    </Badge>
                    <Badge fontSize="9px" colorScheme={m.origem === 'correcao' ? 'purple' : 'gray'}>
                      {m.origem}
                    </Badge>
                    <Badge
                      fontSize="9px"
                      colorScheme={m.status === 'confiavel' ? 'green' : 'orange'}
                    >
                      {m.status}
                    </Badge>
                  </Flex>
                  <Text fontSize="11px" color={C.muted}>
                    POC {m.poc_codigo ?? '—'} · suporte {m.suporte} · pureza{' '}
                    {(m.pureza * 100).toFixed(0)}% · {fmtData(m.dt_atualizacao)}
                  </Text>
                </Box>
                {m.status === 'quarentena' && (
                  <Button
                    size="xs"
                    bg={C.violet}
                    color="white"
                    _hover={{ filter: 'brightness(1.08)' }}
                    isLoading={promover.isPending}
                    onClick={() => handlePromover(m.memoria_id)}
                  >
                    Promover
                  </Button>
                )}
              </Flex>
            ))}
          </Flex>
        </GlassCard>
      )}

      {/* Regras decaindo */}
      {data && data.regras_decaindo.length > 0 && (
        <GlassCard intensity="medium" p={5}>
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <Text fontWeight="700" color={C.ink}>
                Regras decaindo
              </Text>
              <Text fontSize="11px" color={C.muted}>
                Revert rate {'>'}25% — usuário tá corrigindo o que essas regras fazem
              </Text>
            </Box>
            <Badge bg="rgba(239,68,68,0.12)" color={C.red} borderRadius="full" px={2}>
              {data.regras_decaindo.length}
            </Badge>
          </Flex>
          <Flex direction="column" gap={2}>
            {data.regras_decaindo.map(r => (
              <Box
                key={r.regra_id}
                p={3}
                borderRadius="lg"
                bg="rgba(239,68,68,0.06)"
                borderWidth="1px"
                borderColor="rgba(239,68,68,0.2)"
              >
                <Flex justify="space-between" align="center" gap={3}>
                  <Box flex={1} minW={0}>
                    <Flex align="center" gap={2}>
                      <Text fontSize="13px" fontWeight="600" color={C.ink} noOfLines={1}>
                        {r.categoria ?? '(sem categoria)'}
                      </Text>
                      {r.criada_por_ia && (
                        <Badge fontSize="9px" colorScheme="purple">
                          IA
                        </Badge>
                      )}
                      {r.validada && (
                        <Badge fontSize="9px" colorScheme="green">
                          validada
                        </Badge>
                      )}
                    </Flex>
                    <Text fontSize="11px" color={C.muted}>
                      kw: {r.kw.join(', ').slice(0, 80)} · POC {r.poc_codigo ?? '—'}
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="18px" fontWeight="700" color={C.red}>
                      {Math.round(r.revert_rate * 100)}%
                    </Text>
                    <Text fontSize="9px" color={C.muted}>
                      {r.qtd_revertida}/{r.qtd_aplicada} revertidos
                    </Text>
                  </Box>
                </Flex>
              </Box>
            ))}
          </Flex>
        </GlassCard>
      )}

      {totalSinais > 0 && (
        <Box>
          <Divider mb={3} />
          <Text fontSize="10px" color={C.subtle} textAlign="center">
            Atualizado em {data ? fmtData(data.gerado_em) : '—'} · Auditor rodou{' '}
            {(data?.drift_memoria.length ?? 0) +
              (data?.anomalias_cluster.length ?? 0) +
              (data?.regras_decaindo.length ?? 0)}{' '}
            detecções
          </Text>
        </Box>
      )}
    </Flex>
  );
}

// ----------------------------------------------------------------------------
// Sprint 8 — Card Memoria Semantica (embeddings Voyage + pgvector)
// ----------------------------------------------------------------------------

function MemoriaSemanticaCard({ empresaId }: { empresaId: number }) {
  const indigo = '#6366F1';
  const toast = useToast();
  const status = useContaCilEmbeddingsStatus(empresaId);
  const reembed = useContaCilEmbeddingsReembed(empresaId);

  const handleReembed = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const r = await reembed.mutateAsync({ limite: 1000 });
      toast({
        status: r.qtd_processada > 0 ? 'success' : 'info',
        title:
          r.qtd_processada > 0 ? `${r.qtd_processada} embeddings atualizados` : 'Tudo indexado',
        description: `${r.qtd_pulada_hash} pulados por cache · modelo ${r.modelo}`,
        duration: 5000,
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast({
        status: 'error',
        title: 'Falha no reembed',
        description: msg ?? 'Cheque VOYAGE_API_KEY no .env',
      });
    }
  };

  const total = status.data?.qtd_total ?? 0;
  const indexado = status.data?.qtd_indexado_modelo_atual ?? 0;
  const pct = total > 0 ? Math.round((indexado / total) * 100) : 0;

  return (
    <GlassCard intensity="medium" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          color={C.subtle}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Memória semântica
        </Text>
        <Badge bg={`${indigo}22`} color={indigo} borderRadius="full" px={2} fontSize="9px">
          {status.data?.modelo_atual ?? 'voyage-3'}
        </Badge>
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={indigo} lineHeight="1">
        {pct}%
      </Text>
      <Text fontSize="11px" color={C.muted} mb={3}>
        {fmtNum(indexado)} / {fmtNum(total)} indexados · dim {status.data?.dim ?? 1024}
      </Text>
      {status.data?.aviso && (
        <Text fontSize="10px" color={C.amber} fontStyle="italic" mb={2}>
          {status.data.aviso}
        </Text>
      )}
      <Box w="full" h="6px" bg="rgba(99,102,241,0.12)" borderRadius="full" overflow="hidden" mb={3}>
        <Box w={`${pct}%`} h="100%" bg={indigo} transition="width 0.3s" />
      </Box>
      <Flex
        pt={2}
        borderTopWidth="1px"
        borderColor="rgba(15,23,42,0.06)"
        align="center"
        justify="space-between"
      >
        <Text fontSize="9px" color={C.subtle}>
          {pct === 100 ? 'completo' : `${total - indexado} faltam`}
        </Text>
        <Button
          size="xs"
          variant="ghost"
          color={indigo}
          fontWeight="600"
          isLoading={reembed.isPending}
          loadingText="Embedando"
          onClick={handleReembed}
          isDisabled={total === 0}
          _hover={{ bg: 'rgba(99,102,241,0.10)' }}
        >
          ⚡ Reembedar {total - indexado > 1000 ? '+1k' : ''}
        </Button>
      </Flex>
    </GlassCard>
  );
}

// ----------------------------------------------------------------------------
// Sprint 9 — Card Aprendizado Coletivo (memoria global cross-empresa)
// ----------------------------------------------------------------------------

function AprendizadoColetivoCard() {
  const sky = '#0EA5E9';
  const toast = useToast();
  const status = useContaCilMemoriaGlobalStatus();
  const agregar = useContaCilMemoriaGlobalAgregar();

  const handleAgregar = async () => {
    try {
      const r = await agregar.mutateAsync();
      toast({
        status: 'success',
        title: `${r.padroes_globais_total} padrões globais`,
        description: 'Memória cross-empresa atualizada.',
        duration: 4000,
      });
    } catch {
      toast({ status: 'error', title: 'Falha ao agregar' });
    }
  };

  const total = status.data?.total ?? 0;
  const qualificados = status.data?.qualificados_pra_ia ?? 0;
  const pct = total > 0 ? Math.round((qualificados / total) * 100) : 0;
  const ultimoStr = status.data?.ultimo_recalc
    ? new Date(status.data.ultimo_recalc).toLocaleDateString('pt-BR')
    : 'nunca';

  return (
    <GlassCard intensity="medium" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          color={C.subtle}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Aprendizado coletivo
        </Text>
        <Badge bg={`${sky}22`} color={sky} borderRadius="full" px={2} fontSize="9px">
          cross-empresa
        </Badge>
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={sky} lineHeight="1">
        {fmtNum(qualificados)}
      </Text>
      <Text fontSize="11px" color={C.muted} mb={3}>
        padrões qualificados pra IA · {fmtNum(total)} total · {pct}% prontos
      </Text>
      {status.data?.top && status.data.top.length > 0 && (
        <Box mb={3}>
          <Text fontSize="9px" color={C.subtle} fontWeight="600" textTransform="uppercase" mb={1}>
            Top consensos
          </Text>
          <Flex direction="column" gap={1}>
            {status.data.top.slice(0, 5).map(t => (
              <Flex key={`${t.contraparte_norm}-${t.dc}`} justify="space-between" fontSize="10px">
                <Text color={C.ink} noOfLines={1} flex={1} minW={0}>
                  {t.contraparte_norm}
                </Text>
                <Text color={C.muted} flexShrink={0} ml={2}>
                  {t.n_empresas} emp · {Math.round(t.pureza * 100)}%
                </Text>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}
      <Flex
        pt={2}
        borderTopWidth="1px"
        borderColor="rgba(15,23,42,0.06)"
        align="center"
        justify="space-between"
      >
        <Text fontSize="9px" color={C.subtle}>
          últ. recalc: {ultimoStr}
        </Text>
        <Button
          size="xs"
          variant="ghost"
          color={sky}
          fontWeight="600"
          isLoading={agregar.isPending}
          loadingText="Agregando"
          onClick={handleAgregar}
          _hover={{ bg: 'rgba(14,165,233,0.10)' }}
        >
          ⚡ Agregar agora
        </Button>
      </Flex>
    </GlassCard>
  );
}

// ----------------------------------------------------------------------------
// Sprint 3 — Aba Prova de Resistencia: golden set + run + historico F1
// ----------------------------------------------------------------------------

function ProvaTab({ empresaId }: { empresaId: number }) {
  const cyan = '#06B6D4';
  const toast = useToast();
  const goldenQ = useContaCilGoldenSet(empresaId);
  const histQ = useContaCilGoldenHistorico(empresaId);
  const remover = useContaCilGoldenRemover();
  const runMut = useContaCilGoldenRun(empresaId);
  const runMotorMut = useContaCilGoldenRunMotor(empresaId);
  const [lastRun, setLastRun] = React.useState<IGoldenRunResult | null>(null);

  const handleRun = async () => {
    try {
      const r = await runMut.mutateAsync();
      setLastRun(r);
      toast({
        status: 'success',
        title: `F1 = ${(r.f1_poc * 100).toFixed(1)}%`,
        description: `${r.qtd_acerto_poc}/${r.qtd_total} POCs acertadas · ${r.qtd_erro} erro(s)`,
        duration: 6000,
      });
    } catch {
      toast({ status: 'error', title: 'Falha na prova' });
    }
  };

  const handleRunMotor = async () => {
    try {
      const r = await runMotorMut.mutateAsync();
      setLastRun(r);
      toast({
        status: 'success',
        title: `Motor: F1 = ${(r.f1_poc * 100).toFixed(1)}%`,
        description: `${r.qtd_acerto_poc}/${r.qtd_total} POCs acertadas · ${r.qtd_erro} erro(s) · 0 tokens`,
        duration: 6000,
      });
    } catch {
      toast({ status: 'error', title: 'Falha na prova do motor' });
    }
  };

  const handleRemover = async (id: number) => {
    try {
      await remover.mutateAsync(id);
      toast({ status: 'success', title: 'Removido do golden' });
    } catch {
      toast({ status: 'error', title: 'Falha ao remover' });
    }
  };

  const total = goldenQ.data?.total ?? 0;
  const histRuns = histQ.data?.runs ?? [];

  return (
    <Flex direction="column" gap={4}>
      {/* Resumo + botão Rodar */}
      <GlassCard intensity="medium" p={5}>
        <Flex align="center" justify="space-between" gap={3} wrap="wrap">
          <Box>
            <Text
              fontSize="11px"
              fontWeight="700"
              color={C.subtle}
              textTransform="uppercase"
              letterSpacing="0.08em"
            >
              Golden Set
            </Text>
            <Text fontSize="26px" fontWeight="700" color={C.ink} lineHeight="1.1">
              {fmtNum(total)} lançamentos curados
            </Text>
            <Text fontSize="12px" color={C.muted}>
              Bateria de teste: a IA (paga) ou o motor (0 tokens) propõe POC sem ver o gabarito,
              depois comparamos — os dois entram no mesmo histórico.
            </Text>
          </Box>
          <Flex gap={2} wrap="wrap">
            <Button
              variant="outline"
              borderColor={cyan}
              color={cyan}
              _hover={{ bg: `${cyan}14` }}
              isLoading={runMotorMut.isPending}
              loadingText="Rodando motor"
              onClick={handleRunMotor}
              isDisabled={total === 0 || runMut.isPending}
              borderRadius="lg"
              px={5}
            >
              Rodar prova (motor)
            </Button>
            <Button
              bg={cyan}
              color="white"
              _hover={{ filter: 'brightness(1.08)' }}
              isLoading={runMut.isPending}
              loadingText="Testando IA"
              onClick={handleRun}
              isDisabled={total === 0 || runMotorMut.isPending}
              borderRadius="lg"
              px={5}
            >
              Rodar prova (IA)
            </Button>
          </Flex>
        </Flex>
        {total === 0 && (
          <Text fontSize="12px" color={C.muted} mt={3}>
            Como curar: na tela <b>Conciliar</b>, vá num lançamento já conciliado e adicione ao
            golden (botão será adicionado no Sprint 4 — por enquanto curar via API).
          </Text>
        )}
      </GlassCard>

      {/* Resultado do último run */}
      {lastRun && (
        <GlassCard intensity="medium" p={5}>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="700" color={C.ink}>
              Resultado do último run
            </Text>
            <Badge bg={`${cyan}22`} color={cyan} borderRadius="full" px={2}>
              {lastRun.modelo ?? '—'}
            </Badge>
          </Flex>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                F1 POC
              </Text>
              <Text fontSize="22px" fontWeight="700" color={cyan}>
                {(lastRun.f1_poc * 100).toFixed(1)}%
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Precision
              </Text>
              <Text fontSize="22px" fontWeight="700" color={C.ink}>
                {(lastRun.precision_poc * 100).toFixed(1)}%
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Recall
              </Text>
              <Text fontSize="22px" fontWeight="700" color={C.ink}>
                {(lastRun.recall_poc * 100).toFixed(1)}%
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Erros
              </Text>
              <Text fontSize="22px" fontWeight="700" color={lastRun.qtd_erro > 0 ? C.red : C.green}>
                {fmtNum(lastRun.qtd_erro)}
              </Text>
            </Box>
          </SimpleGrid>
          {/* Onde errou */}
          {lastRun.detalhe.filter(d => !d.acertou_poc).length > 0 && (
            <Box>
              <Text fontSize="12px" fontWeight="600" color={C.ink} mb={2}>
                Onde errou:
              </Text>
              <Flex direction="column" gap={1.5} maxH="300px" overflowY="auto">
                {lastRun.detalhe
                  .filter(d => !d.acertou_poc)
                  .slice(0, 50)
                  .map(d => (
                    <Flex
                      key={d.golden_id}
                      p={2}
                      borderRadius="md"
                      bg="rgba(239,68,68,0.06)"
                      borderWidth="1px"
                      borderColor="rgba(239,68,68,0.18)"
                      fontSize="11px"
                      gap={2}
                    >
                      <Text flex={1} minW={0} noOfLines={1} color={C.ink}>
                        {d.historico}
                      </Text>
                      <Text color={C.muted}>
                        <b>{d.esperado_poc ?? '—'}</b> →{' '}
                        <span style={{ color: C.red }}>{d.predito_poc ?? 'vazio'}</span>
                      </Text>
                    </Flex>
                  ))}
              </Flex>
            </Box>
          )}
        </GlassCard>
      )}

      {/* Histórico de F1 */}
      {histRuns.length > 0 && (
        <GlassCard intensity="medium" p={5}>
          <Text fontWeight="700" color={C.ink} mb={2}>
            Histórico (últimos {histRuns.length})
          </Text>
          <Box mb={2}>
            <MiniSparkline pts={[...histRuns].reverse().map(r => r.f1_poc * 100)} color={cyan} />
          </Box>
          <Flex direction="column" gap={1}>
            {histRuns.slice(0, 8).map((r: IGoldenRunHistoricoItem) => (
              <Flex key={r.id} justify="space-between" align="center" fontSize="11px">
                <Text color={C.muted}>
                  {fmtData(r.dt_criacao)} · {r.modelo ?? '—'}
                </Text>
                <Flex gap={3}>
                  <Text color={C.muted}>
                    {r.qtd_acerto_poc}/{r.qtd_total}
                  </Text>
                  <Text fontWeight="700" color={cyan}>
                    F1 {(r.f1_poc * 100).toFixed(1)}%
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </GlassCard>
      )}

      {/* Itens do golden */}
      {goldenQ.data && goldenQ.data.itens.length > 0 && (
        <GlassCard intensity="medium" p={5}>
          <Text fontWeight="700" color={C.ink} mb={3}>
            Lançamentos no golden
          </Text>
          <Flex direction="column" gap={2} maxH="500px" overflowY="auto">
            {goldenQ.data.itens.map(g => (
              <Flex
                key={g.id}
                p={3}
                borderRadius="lg"
                bg="rgba(255,255,255,0.55)"
                borderWidth="1px"
                borderColor="rgba(15,23,42,0.06)"
                gap={2}
                align="center"
              >
                <Box flex={1} minW={0}>
                  <Flex align="center" gap={2}>
                    <Badge fontSize="9px" colorScheme={g.dc === 'D' ? 'red' : 'green'}>
                      {g.dc}
                    </Badge>
                    <Text fontSize="13px" fontWeight="600" color={C.ink} noOfLines={1}>
                      {g.historico_snapshot}
                    </Text>
                  </Flex>
                  <Text fontSize="11px" color={C.muted}>
                    R$ {g.valor.toFixed(2)} · POC esperada:{' '}
                    <b>{g.dc === 'D' ? g.debito_poc_label : g.credito_poc_label}</b>
                    {g.motivo && ` · ${g.motivo}`}
                  </Text>
                </Box>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemover(g.id)}
                >
                  Remover
                </Button>
              </Flex>
            ))}
          </Flex>
        </GlassCard>
      )}

      {/* Sprint 5 — Comparacao A/B entre runs */}
      {histRuns.length >= 2 && <ComparacaoAB empresaId={empresaId} runs={histRuns} />}
    </Flex>
  );
}

// Sprint 5 — Diff entre 2 runs do golden (regressao vs melhoria).
function ComparacaoAB({ empresaId, runs }: { empresaId: number; runs: IGoldenRunHistoricoItem[] }) {
  // Defaults: A = penultimo, B = ultimo (lista vem desc → [0]=ultimo).
  const [runA, setRunA] = React.useState<number>(runs[1]?.id ?? 0);
  const [runB, setRunB] = React.useState<number>(runs[0]?.id ?? 0);
  // Ressincroniza quando a lista muda (run novo / troca de empresa): estado
  // veio de props e ficaria preso na seleção antiga. Só reseta se a seleção
  // atual não existe mais na lista — preserva escolha manual válida.
  React.useEffect(() => {
    const ids = new Set(runs.map(r => r.id));
    if (!ids.has(runA) || !ids.has(runB)) {
      setRunA(runs[1]?.id ?? 0);
      setRunB(runs[0]?.id ?? 0);
    }
  }, [runs, runA, runB]);
  const comp = useContaCilGoldenComparar(empresaId, runA, runB);

  const fmtRunOpt = (r: IGoldenRunHistoricoItem) =>
    `#${r.id} · ${new Date(r.dt_criacao).toLocaleDateString('pt-BR')} · F1 ${(r.f1_poc * 100).toFixed(0)}%`;

  return (
    <GlassCard intensity="medium" p={5}>
      <Text fontWeight="700" color={C.ink} mb={3}>
        Comparar runs (A vs B)
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
        <Box>
          <Text fontSize="10px" color={C.subtle} fontWeight="600" textTransform="uppercase" mb={1}>
            Run A (referência)
          </Text>
          <select
            value={runA}
            onChange={e => setRunA(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '6px 8px',
              borderRadius: 8,
              border: '1px solid rgba(15,23,42,0.12)',
              fontSize: 12,
            }}
          >
            {runs.map(r => (
              <option key={r.id} value={r.id}>
                {fmtRunOpt(r)}
              </option>
            ))}
          </select>
        </Box>
        <Box>
          <Text fontSize="10px" color={C.subtle} fontWeight="600" textTransform="uppercase" mb={1}>
            Run B (comparar)
          </Text>
          <select
            value={runB}
            onChange={e => setRunB(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '6px 8px',
              borderRadius: 8,
              border: '1px solid rgba(15,23,42,0.12)',
              fontSize: 12,
            }}
          >
            {runs.map(r => (
              <option key={r.id} value={r.id}>
                {fmtRunOpt(r)}
              </option>
            ))}
          </select>
        </Box>
      </SimpleGrid>

      {runA === runB && (
        <Text fontSize="11px" color={C.muted}>
          Escolha dois runs diferentes.
        </Text>
      )}

      {comp.data && (
        <>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Δ F1
              </Text>
              <Text
                fontSize="22px"
                fontWeight="700"
                color={comp.data.delta_f1 >= 0 ? C.green : C.red}
              >
                {comp.data.delta_f1 >= 0 ? '+' : ''}
                {(comp.data.delta_f1 * 100).toFixed(1)}pp
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Regressões
              </Text>
              <Text
                fontSize="22px"
                fontWeight="700"
                color={comp.data.qtd_regressoes > 0 ? C.red : C.subtle}
              >
                {fmtNum(comp.data.qtd_regressoes)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Melhorias
              </Text>
              <Text fontSize="22px" fontWeight="700" color={C.green}>
                {fmtNum(comp.data.qtd_melhorias)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Estável OK
              </Text>
              <Text fontSize="22px" fontWeight="700" color={C.ink}>
                {fmtNum(comp.data.qtd_estavel_ok)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color={C.subtle}>
                Mudou resp.
              </Text>
              <Text fontSize="22px" fontWeight="700" color={C.amber}>
                {fmtNum(comp.data.qtd_mudou_resposta)}
              </Text>
            </Box>
          </SimpleGrid>

          {/* Listar regressões (mais importante) */}
          {comp.data.qtd_regressoes > 0 && (
            <Box>
              <Text fontSize="12px" fontWeight="600" color={C.red} mb={2}>
                Regressões — A acertou, B errou:
              </Text>
              <Flex direction="column" gap={1.5} maxH="240px" overflowY="auto">
                {comp.data.itens
                  .filter(i => i.tipo === 'regressao')
                  .slice(0, 30)
                  .map(i => (
                    <Flex
                      key={i.golden_id}
                      p={2}
                      borderRadius="md"
                      bg="rgba(239,68,68,0.06)"
                      borderWidth="1px"
                      borderColor="rgba(239,68,68,0.2)"
                      fontSize="11px"
                      gap={2}
                    >
                      <Text flex={1} minW={0} noOfLines={1}>
                        {i.historico}
                      </Text>
                      <Text color={C.muted}>
                        A: <b style={{ color: C.green }}>{i.predito_a ?? '—'}</b>
                        {' → '}
                        B: <b style={{ color: C.red }}>{i.predito_b ?? 'vazio'}</b>
                      </Text>
                    </Flex>
                  ))}
              </Flex>
            </Box>
          )}

          {comp.data.qtd_melhorias > 0 && (
            <Box mt={3}>
              <Text fontSize="12px" fontWeight="600" color={C.green} mb={2}>
                Melhorias — B acertou onde A errava:
              </Text>
              <Flex direction="column" gap={1.5} maxH="180px" overflowY="auto">
                {comp.data.itens
                  .filter(i => i.tipo === 'melhoria')
                  .slice(0, 20)
                  .map(i => (
                    <Flex
                      key={i.golden_id}
                      p={2}
                      borderRadius="md"
                      bg="rgba(16,185,129,0.06)"
                      borderWidth="1px"
                      borderColor="rgba(16,185,129,0.2)"
                      fontSize="11px"
                      gap={2}
                    >
                      <Text flex={1} minW={0} noOfLines={1}>
                        {i.historico}
                      </Text>
                      <Text color={C.muted}>
                        A: <b style={{ color: C.red }}>{i.predito_a ?? 'vazio'}</b>
                        {' → '}
                        B: <b style={{ color: C.green }}>{i.predito_b ?? '—'}</b>
                      </Text>
                    </Flex>
                  ))}
              </Flex>
            </Box>
          )}
        </>
      )}
    </GlassCard>
  );
}

function ProgressoTab({ empresaId }: { empresaId: number }) {
  const resumoQ = useContaCilResumo({ empresaId, page: 1, size: 1 });
  const porFonte = resumoQ.data?.quebras.por_fonte_match ?? [];

  return (
    <Flex direction="column" gap={5}>
      {resumoQ.data?.resumo && (
        <GlassCard intensity="medium" p={5}>
          <Text fontSize="13px" fontWeight="600" color={C.ink} mb={3}>
            Conciliação geral
          </Text>
          <ProgressoConciliacao resumo={resumoQ.data.resumo} />
        </GlassCard>
      )}

      <GlassCard intensity="medium" p={5}>
        <Text fontSize="13px" fontWeight="600" color={C.ink} mb={1}>
          Conciliações por fonte
        </Text>
        <Text fontSize="11px" color={C.muted} mb={3}>
          De onde veio cada conciliação — quanto a IA já resolve sozinha.
        </Text>
        {porFonte.length === 0 ? (
          <Text fontSize="sm" color={C.subtle}>
            Nada conciliado ainda.
          </Text>
        ) : (
          <Flex direction="column" gap={2}>
            {porFonte.map(f => (
              <Flex key={f.rotulo} align="center" gap={3}>
                <Text fontSize="12px" color={C.ink} flex={1} minW={0} noOfLines={1}>
                  {f.rotulo}
                </Text>
                <Text fontSize="12px" fontWeight="600" color={C.muted}>
                  {fmtNum(f.qtd_conciliado)} / {fmtNum(f.qtd)}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </GlassCard>
    </Flex>
  );
}

export function ConciliacaoContabilSchoolAiPage() {
  const navigate = useNavigate();
  const { isTechnical } = useAuth();
  const [empresa, setEmpresa] = React.useState<IEmpresaOption | null>(null);
  useAplicarEmpresaParam(setEmpresa);
  const [tab, setTab] = React.useState<Tab>('residuo');

  // Badge do tab "Residuo IA" usa o residuo do progresso (mantido por
  // compatibilidade — Sprint 2 vai substituir por count direto).
  const prog = useContaCilIaProgresso(empresa?.value);
  // Sprint 1 — agregado novo (4 cards em 1 request).
  const dash = useContaCilSchoolAiDashboard(empresa?.value);

  // School AI é ferramenta técnica — só Admin (1) / Desenvolvimento (5).
  if (!isTechnical()) {
    return (
      <GlassPageBg>
        <Box maxW="640px" mx="auto" w="full" px={6} py={20}>
          <GlassCard intensity="medium" p={10}>
            <Text fontSize="md" fontWeight="600" color={C.ink} textAlign="center" mb={1}>
              Acesso restrito
            </Text>
            <Text fontSize="sm" color={C.muted} textAlign="center">
              O School AI é uma ferramenta técnica — disponível apenas para Administração e
              Desenvolvimento.
            </Text>
          </GlassCard>
        </Box>
      </GlassPageBg>
    );
  }

  const abas: [Tab, string, string][] = [
    ['residuo', 'Resíduo IA', C.amber],
    ['fila', 'Fila de sugestões', C.green],
    ['aprendeu', 'O que a IA aprendeu', C.violet],
    ['divergencias', 'Regra × razão', C.red],
    ['auditor', 'Auditor', C.red],
    ['prova', 'Prova de Resistência', '#06B6D4'],
    ['progresso', 'Progresso', C.green],
  ];

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={5}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color={C.muted}
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: C.ink }}
              onClick={() => navigate('/modulesHome/accountingConciliation/conciliacao-contabil')}
            />
            <Flex
              w="52px"
              h="52px"
              borderRadius="2xl"
              align="center"
              justify="center"
              color="white"
              bgGradient={`linear(135deg, ${C.violet}, #6366F1)`}
              boxShadow="0 8px 24px -4px rgba(139,92,246,0.4)"
            >
              <GraduationCap size={28} weight="fill" />
            </Flex>
            <Box>
              <Text
                fontSize="11px"
                color={C.subtle}
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Conciliação Contábil
              </Text>
              <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color={C.ink}>
                School AI
              </Heading>
            </Box>
          </Flex>
        </motion.div>

        <GlassCard intensity="medium" p={4} mb={5}>
          <EmpresaPicker value={empresa} onChange={setEmpresa} />
        </GlassCard>

        {!empresa ? (
          <GlassCard intensity="medium" p={10}>
            <Text fontSize="sm" color={C.muted} textAlign="center">
              Selecione uma empresa para ver o que a IA aprendeu, o resíduo e o progresso.
            </Text>
          </GlassCard>
        ) : (
          <>
            {/* Sprint 1 — Dashboard home: 4 cards + Sprint 8 Memória semântica */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3} mb={3}>
              {dash.isError && (
                <GlassCard intensity="medium" p={4} gridColumn="1 / -1">
                  <Flex align="center" gap={2}>
                    <WarningCircle size={16} color="#EF4444" weight="fill" />
                    <Text fontSize="sm" color={C.ink} fontWeight="600">
                      Não foi possível carregar o dashboard.
                    </Text>
                  </Flex>
                  <Text fontSize="12px" color={C.muted} mt={1}>
                    Verifique a conexão e recarregue a página.
                  </Text>
                </GlassCard>
              )}
              {!dash.isError &&
                (dash.data ? (
                  <>
                    <HojeCard data={dash.data.hoje} />
                    <AtencaoCard data={dash.data.atencao} onClick={() => setTab('aprendeu')} />
                    <AprendizadoCard
                      key={empresa.value}
                      data={dash.data.aprendizado}
                      onClick={() => setTab('aprendeu')}
                      empresaId={empresa.value}
                    />
                    <TendenciaCard serie={dash.data.serie_30d} />
                  </>
                ) : (
                  <>
                    <GlassCard intensity="medium" p={4}>
                      <Text fontSize="11px" color={C.subtle}>
                        Carregando…
                      </Text>
                    </GlassCard>
                    <GlassCard intensity="medium" p={4}>
                      <Text fontSize="11px" color={C.subtle}>
                        Carregando…
                      </Text>
                    </GlassCard>
                    <GlassCard intensity="medium" p={4}>
                      <Text fontSize="11px" color={C.subtle}>
                        Carregando…
                      </Text>
                    </GlassCard>
                    <GlassCard intensity="medium" p={4}>
                      <Text fontSize="11px" color={C.subtle}>
                        Carregando…
                      </Text>
                    </GlassCard>
                  </>
                ))}
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={5}>
              <MemoriaSemanticaCard empresaId={empresa.value} />
              <AprendizadoColetivoCard />
            </SimpleGrid>

            <Flex
              display="inline-flex"
              bg="rgba(255,255,255,0.72)"
              backdropFilter="blur(20px) saturate(180%)"
              sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
              borderRadius="full"
              p="4px"
              borderWidth="1px"
              borderColor="rgba(255,255,255,0.6)"
              mb={5}
              gap="2px"
            >
              {abas.map(([val, label, color]) => (
                <Button
                  key={val}
                  size="sm"
                  h="34px"
                  px={4}
                  borderRadius="full"
                  variant="unstyled"
                  fontWeight="600"
                  fontSize="13px"
                  bg={tab === val ? 'white' : 'transparent'}
                  color={tab === val ? color : C.muted}
                  boxShadow={tab === val ? '0 2px 8px -2px rgba(15,23,42,0.12)' : undefined}
                  onClick={() => setTab(val)}
                >
                  {label}
                  {val === 'residuo' && (prog.data?.residuo ?? 0) > 0 ? (
                    <Badge ml={2} borderRadius="full" colorScheme="orange" fontSize="9px">
                      {fmtNum(prog.data?.residuo ?? 0)}
                    </Badge>
                  ) : null}
                </Button>
              ))}
            </Flex>

            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.24 }}
              >
                {/* key={empresa.value}: troca de empresa REMONTA a aba —
                    sem isso, estado interno (resultado de prova, seleção
                    A/B, stats do autopromover) sobrevivia à troca e exibia
                    dado da empresa anterior. */}
                {tab === 'residuo' && <ResiduoTab key={empresa.value} empresaId={empresa.value} />}
                {tab === 'fila' && (
                  <FilaSugeridosTab key={empresa.value} empresaId={empresa.value} />
                )}
                {tab === 'aprendeu' && <AbaMemoria key={empresa.value} empresaId={empresa.value} />}
                {tab === 'divergencias' && (
                  <DivergenciasTab key={empresa.value} empresaId={empresa.value} />
                )}
                {tab === 'auditor' && <AuditorTab key={empresa.value} empresaId={empresa.value} />}
                {tab === 'prova' && <ProvaTab key={empresa.value} empresaId={empresa.value} />}
                {tab === 'progresso' && (
                  <ProgressoTab key={empresa.value} empresaId={empresa.value} />
                )}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </Box>
    </GlassPageBg>
  );
}
